import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { CASES as STATIC_CASES } from "@/data/cases";
import { publicApi, resolveAssetUrl } from "@/lib/api";

export default function Projects() {
  const [cases, setCases] = useState(STATIC_CASES);

  useEffect(() => {
    let mounted = true;
    publicApi
      .get("/cases")
      .then(({ data }) => {
        if (!mounted) return;
        if (Array.isArray(data) && data.length > 0) {
          setCases(data);
        }
      })
      .catch(() => {
        // keep static fallback
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!cases || cases.length === 0) return null;

  return (
    <section
      id="projekte"
      data-testid="projects-section"
      className="py-24 md:py-32 border-b-2 border-black"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14 md:mb-20">
          <div>
            <p className="font-mono-acc text-xs text-[#525252] mb-4">// 03 — Projekte</p>
            <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
              Bisherige<br /><span className="text-[#FF3B00]">Projekte.</span>
            </h2>
          </div>
          <p className="font-mono-acc text-[11px] md:text-xs text-[#525252] max-w-sm">
            Auswahl an Kampagnen und Projekten — detaillierte Insights auf Anfrage.
          </p>
        </div>

        <div
          className={`grid grid-cols-1 gap-px bg-black border-2 border-black ${
            cases.length === 2
              ? "md:grid-cols-2"
              : cases.length === 4
              ? "md:grid-cols-2"
              : cases.length >= 3
              ? "md:grid-cols-2 lg:grid-cols-3"
              : ""
          }`}
        >
          {cases.map((c, i) => (
            <article
              key={c.id}
              data-testid={`project-card-${i}`}
              className="group bg-[#F4F4F0] flex flex-col"
            >
              <div className="p-6 md:p-8 flex flex-col flex-1 border-t-8 border-[#FF3B00]">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-mono-acc text-[10px] text-[#0F0F0F]">
                    {c.number ? `Case ${c.number}` : ""}
                    {c.number && c.period ? " · " : ""}
                    {c.period || ""}
                  </span>
                  <span className="font-mono-acc text-[10px] text-[#525252]">{c.role || ""}</span>
                </div>
                <h3 className="mt-3 font-display uppercase text-xl md:text-2xl leading-tight">
                  {c.title}
                </h3>
                {c.brand && (
                  <p className="mt-1 font-mono-acc text-[11px] text-[#FF3B00]">{c.brand}</p>
                )}
                {c.description && (
                  <p className="mt-4 text-[15px] md:text-base text-[#0F0F0F] leading-relaxed">
                    {c.description}
                  </p>
                )}

                {Array.isArray(c.results) && c.results.length > 0 && (
                  <ul className="mt-5 space-y-2">
                    {c.results.map((r, j) => (
                      <li
                        key={j}
                        data-testid={`project-${i}-result-${j}`}
                        className="flex items-start gap-2 font-display uppercase text-sm md:text-base"
                      >
                        <span className="text-[#FF3B00]">→</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {Array.isArray(c.tags) && c.tags.length > 0 && (
                  <div className="mt-6 pt-5 border-t-2 border-black flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-2">
                      {c.tags.map((t) => (
                        <span
                          key={t}
                          className="font-mono-acc text-[10px] px-2 py-1 border-2 border-black bg-[#F4F4F0] group-hover:bg-black group-hover:text-[#F4F4F0] transition-colors"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    {c.link && (
                      <a
                        href={c.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`project-link-${i}`}
                        aria-label={`Externer Link zu ${c.title}`}
                        className="inline-flex items-center justify-center w-8 h-8 bg-[#FF3B00] text-white border-2 border-black hover:bg-black hover:text-[#FF3B00] transition-colors"
                      >
                        <ArrowUpRight className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
